import React from "react";
import { Space, Tag, theme, Typography } from "antd";

const { Title, Text } = Typography;

export interface PageHeaderMetaItem {
  label: string;
  value: React.ReactNode;
}

export interface PageHeaderAction {
  node: React.ReactNode;
  key: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Ant Design Tag preset color or custom style */
  status?: string;
  statusColor?: string;
  actions?: React.ReactNode[];
  metadata?: PageHeaderMetaItem[];
  /** Render a custom element below the title row (e.g. secondary toolbar) */
  extra?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  status,
  statusColor,
  actions = [],
  metadata = [],
  extra,
}) => {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        paddingBottom: 10,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {/* Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Title
            level={4}
            style={{ margin: 0, fontSize: 16, fontWeight: 600, color: token.colorText, lineHeight: 1.3 }}
          >
            {title}
          </Title>

          {status && (
            <Tag
              color={statusColor ?? "processing"}
              style={{ margin: 0, fontSize: 11, fontWeight: 500, borderRadius: token.borderRadius }}
            >
              {status}
            </Tag>
          )}
        </div>

        {actions.length > 0 && (
          <Space size={8} wrap>
            {actions.map((action, i) => (
              <React.Fragment key={i}>{action}</React.Fragment>
            ))}
          </Space>
        )}
      </div>

      {/* Subtitle and Metadata Row */}
      {(subtitle || metadata.length > 0) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", lineHeight: 1.4 }}>
          {subtitle && (
            <Text style={{ fontSize: 12, color: token.colorTextSecondary, margin: 0 }}>
              {subtitle}
            </Text>
          )}
          {subtitle && metadata.length > 0 && (
            <span style={{ color: token.colorTextQuaternary, fontSize: 11, userSelect: "none" }}>•</span>
          )}
          {metadata.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", alignItems: "center" }}>
              {metadata.map((item, i) => (
                <span key={i} style={{ fontSize: 11, color: token.colorTextSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: token.colorTextDescription }}>{item.label}:</span>{" "}
                  <span>{item.value}</span>
                  {i < metadata.length - 1 && <span style={{ color: token.colorTextQuaternary, marginLeft: 8, userSelect: "none" }}>|</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {extra && <div style={{ marginTop: 4 }}>{extra}</div>}
    </div>
  );
};

export default PageHeader;
